import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';

/**
 * Hook for fetching current user's strike data from /attendance/my-strikes endpoint.
 *
 * Returns strike count, history, and lock status for display in My Attendance tab.
 *
 * @returns {Object} Strike data and loading state
 * @property {Object} strikes - Strike data from API (strike_count, max_strikes, is_locked, strike_history)
 * @property {boolean} loading - Whether data is being fetched
 * @property {string|null} error - Error message if fetch failed
 * @property {Function} refetch - Manual refetch function
 */
export function useMyStrikes() {
  const [strikes, setStrikes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStrikes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/attendance/daily/strikes');
      setStrikes(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load strike data');
      console.error('Strike fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStrikes();
  }, [fetchStrikes]);

  return { strikes, loading, error, refetch: fetchStrikes };
}
